import { MetricDetails } from '../../../../../../api/metrics/types';
import { DomainPreferences } from '../../../../../../api/types';
import { MetricId } from '../../../../../../api/types-graphql';
import { MetricIdType } from '../../../../../../types';
import { DataFieldValueFormatter } from '../../../../../hooks';
import { SortTypes } from '../../../../../types';
import { removeDuplicates } from '../../../../../utils';
import {
  ChartData,
  DataMapValue,
  MultiDatasetChartData,
  SortedLabels,
} from '../../../../ui-components/data-viz-ui/charts/common/common-chart-types';
import { UnsegmentedResponse } from '../../../common/types';
import { getChartDataFromUnsegmentedResponse } from '../../../common/utils';
import { TimePeriodUnsegmentedVizDataResponse } from '../../types';

export const getChartDataFromTimePeriodUnsegmentedResponse = (
  response: TimePeriodUnsegmentedVizDataResponse,
  preferences: DomainPreferences,
  metricDetailsMap: Partial<Record<MetricId, MetricDetails>>,
  metricDisplayValues: Partial<Record<MetricId, string>>,
  dataFieldValueFormatter: DataFieldValueFormatter
): MultiDatasetChartData => {
  // To simplify, we get unsegmented responses for each result, and then combine them
  const responseData = response.responseForMetrics.map((metricResult) => {
    const unsegmentedResponse: UnsegmentedResponse = {
      metric: metricResult.metric,
      groupBy: response.groupBy,
      isSegmented: false,
      groups: metricResult.groups,
      metricSql: metricResult.metricSql,
    };
    return {
      ...getChartDataFromUnsegmentedResponse(
        unsegmentedResponse,
        preferences,
        metricDetailsMap,
        metricDisplayValues,
        dataFieldValueFormatter
      ),
      metric: metricResult.metric,
    };
  }) as (ChartData & {
    metric: MetricIdType;
  })[];
  const combinedDataMap: Record<string, DataMapValue[]> = {};
  responseData.forEach((data) => {
    Object.entries(data.dataMap).forEach(([label, values]) => {
      // since metric response contains a single dataset
      const value = values[0];
      combinedDataMap[label] = [
        ...(combinedDataMap[label] ?? []),
        {
          datasetKey: value.datasetKey,
          value: value.value,
        },
      ];
    });
  });
  const combinedSortedLabels: SortedLabels = {
    [SortTypes.ASC]: removeDuplicates(
      responseData.flatMap((data) => data.sortedLabels?.[SortTypes.ASC] ?? [])
    ),
    [SortTypes.DESC]: removeDuplicates(
      responseData.flatMap((data) => data.sortedLabels?.[SortTypes.DESC] ?? [])
    ),
  };
  const combinedDatasets = responseData.map((data) => data.datasets[0]);
  const combinedLabels = Object.keys(combinedDataMap);
  const chartData: MultiDatasetChartData = {
    labels: combinedLabels,
    sortedLabels: combinedSortedLabels,
    dataMap: combinedDataMap,
    datasets: combinedDatasets,
    type: 'multi-dataset',
    // False because these are different queries, so they are not stackable
    isStackable: false,
  };
  return chartData;
};
